import { useRef } from "react";

/**
 * Returns `value` live while `isActive` is true, then holds (freezes) the
 * last active value once `isActive` switches to false.
 *
 * Solves the common problem where a parent clears its state the moment a
 * panel closes (e.g. `setDataItem(null)`), causing the form content to flash
 * to empty during the 250ms slide-out CSS animation.
 *
 * Pass the panel's open flag as `isActive` so the value stays live while
 * the panel is open and becomes frozen while it's animating out.
 *
 * @example
 * const { isOpen, isMounted, close } = useSlidePanelState();
 * const frozenPolicy = useFrozenValue(dataItem, isOpen);
 *
 * return isMounted ? (
 *   <MyPanel open={isOpen} onClose={close} policy={frozenPolicy} />
 * ) : null;
 */
export function useFrozenValue<T>(value: T, isActive: boolean): T {
  const ref = useRef<T>(value);
  if (isActive) ref.current = value;
  return ref.current;
}
